import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Constante } from '../../constante';
import { GoogleConfigApi } from '../models/api/auth-api';
import { ResponseApi } from '../models/response-api';
import { AppUtil } from '../utility/app-util';

interface GoogleCredentialResponse {
    credential?: string;
}

interface GooglePromptNotification {
    isNotDisplayed(): boolean;
    isSkippedMoment(): boolean;
    isDismissedMoment(): boolean;
}

interface GoogleAccountsId {
    initialize(config: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        ux_mode?: string;
        context?: string;
        itp_support?: boolean;
        use_fedcm_for_prompt?: boolean;
    }): void;
    prompt(callback?: (notification: GooglePromptNotification) => void): void;
    renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
}

@Injectable({ providedIn: 'root' })
export class GoogleIdentityService {
    private http = inject(HttpClient);
    private url = AppUtil.addTrailingSlash(environment.URL_API_PEDIDOS) + 'api/auth/';
    private clientId: string | null = null;
    private scriptPromise: Promise<void> | null = null;

    isReady(): boolean {
        return !!this.clientId && !!this.googleIdSafe();
    }

    async warmUp(): Promise<void> {
        await this.resolveClientId();
        await this.loadScript();
    }

    async requestCredential(host?: HTMLElement): Promise<string> {
        const clientId = this.clientId ?? await this.resolveClientId();
        if (!this.googleIdSafe()) {
            await this.loadScript();
        }
        const api = this.googleId();

        return new Promise<string>((resolve, reject) => {
            let settled = false;
            const finish = (credential: string) => {
                if (settled) return;
                settled = true;
                resolve(credential);
            };
            const fail = (message: string) => {
                if (settled) return;
                settled = true;
                reject(new Error(message));
            };

            api.initialize({
                client_id: clientId,
                auto_select: false,
                ux_mode: 'popup',
                context: 'signin',
                itp_support: true,
                callback: (response) => {
                    if (response?.credential) {
                        finish(response.credential);
                        return;
                    }
                    fail('Google no devolvió una credencial.');
                }
            });

            if (host) {
                host.replaceChildren();
                api.renderButton(host, {
                    type: 'standard',
                    theme: 'outline',
                    size: 'large',
                    text: 'continue_with',
                    shape: 'pill',
                    locale: 'es',
                    width: 320
                });
            }

            api.prompt((notification) => {
                if (settled) return;
                if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                    const button = host?.querySelector('div[role="button"]') as HTMLElement | null;
                    if (button) {
                        button.click();
                        return;
                    }
                    fail('No se pudo abrir el inicio con Google.');
                }
            });
        });
    }

    private async resolveClientId(): Promise<string> {
        if (this.clientId) {
            return this.clientId;
        }

        try {
            const res = await firstValueFrom(
                this.http.get<ResponseApi<GoogleConfigApi>>(this.url + 'google-config')
            );
            if (res.success === Constante.REQUEST_STATUS_OK && res.data?.clientId) {
                this.clientId = res.data.clientId;
                return this.clientId;
            }
        } catch {
            undefined;
        }

        if (environment.GOOGLE_CLIENT_ID) {
            this.clientId = environment.GOOGLE_CLIENT_ID;
            return this.clientId ?? '';
        }

        throw new Error('Google no está configurado.');
    }

    private loadScript(): Promise<void> {
        if (this.googleIdSafe()) {
            return Promise.resolve();
        }
        if (this.scriptPromise) {
            return this.scriptPromise;
        }

        this.scriptPromise = new Promise<void>((resolve, reject) => {
            const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]') as HTMLScriptElement | null;
            if (existing) {
                existing.addEventListener('load', () => resolve());
                existing.addEventListener('error', () => reject(new Error('No se pudo cargar Google.')));
                if (this.googleIdSafe()) {
                    resolve();
                }
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('No se pudo cargar Google.'));
            document.head.appendChild(script);
        });

        return this.scriptPromise;
    }

    private googleId(): GoogleAccountsId {
        const api = this.googleIdSafe();
        if (!api) {
            throw new Error('Google Identity no está disponible.');
        }
        return api;
    }

    private googleIdSafe(): GoogleAccountsId | null {
        const google = (window as unknown as { google?: { accounts?: { id?: GoogleAccountsId } } }).google;
        return google?.accounts?.id ?? null;
    }
}
